import { Badge } from "@/components/ui/badge";
import { Sheet, SheetBody, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { JsonTree } from "@/components/json-tree";
import { formatDate, formatDateTimeMst, relativeTime } from "@/lib/format-date";
import type { SensorRecord } from "@/api/types";
import { formatHealthConnectJsonValue, getVisibleRecordValues } from "./utils";

interface RecordSheetProps {
  record: SensorRecord | null;
  participantLabel: string;
  deviceLabel: string;
  onClose: () => void;
}

function parseCoordinate(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function getAtLocationCoordinates(record: SensorRecord | null) {
  if (!record || record.source !== "AT_LOCATION") return null;

  const latitude = parseCoordinate(record.values.latitude);
  const longitude = parseCoordinate(record.values.longitude);

  if (latitude === null || longitude === null) return null;
  return { latitude, longitude };
}

function formatAnswer(value: unknown, questions: Record<string, string>, key: string): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") {
    if (value === "NA" && (questions[`${key}__type`] === "rating" || questions[`${key}__opt_NA`])) return "N/A";
    return questions[`${key}__opt_${value}`] ?? value;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    // multiselect: { selected: string[], other: string | null }
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.selected)) {
      // Resolve option ids to the labels the participant saw via the question
      // snapshot; the "__other" sentinel is rendered by `other` below.
      const labels = (obj.selected as unknown[])
        .filter((id) => id !== "__other")
        .map((id) => questions[`${key}__opt_${String(id)}`] ?? String(id));
      if (obj.other) labels.push(`Other: ${String(obj.other)}`);
      return labels.length > 0 ? labels.join(", ") : "—";
    }
  }
  return String(value);
}

const SURVEY_META_KEYS = new Set(["slot_id", "config_version"]);

/** Survey records carry a snapshot of the question text the participant saw;
 *  join it with the answers. Old records without a snapshot fall back to ids. */
function getSurveyResponses(record: SensorRecord) {
  const questions = record.questions ?? {};
  return Object.entries(record.values)
    .filter(([key]) => !SURVEY_META_KEYS.has(key))
    .map(([key, value]) => ({
      key,
      question: questions[key] ?? key,
      answer: formatAnswer(value, questions, key),
    }));
}

export function RecordSheet({ record, participantLabel, deviceLabel, onClose }: RecordSheetProps) {
  const isSurvey = record?.source?.toLowerCase() === "survey";
  const isHealthConnect = record?.source?.toUpperCase() === "HEALTH_CONNECT";
  const visibleValues = getVisibleRecordValues(record);
  const valuesForDisplay =
    record && isHealthConnect
      ? (formatHealthConnectJsonValue(visibleValues) as Record<string, unknown>)
      : visibleValues;
  const coordinates = getAtLocationCoordinates(record);
  const bbox =
    coordinates
      ? `${coordinates.longitude - 0.01},${coordinates.latitude - 0.01},${coordinates.longitude + 0.01},${coordinates.latitude + 0.01}`
      : null;
  const mapSrc =
    coordinates && bbox
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${coordinates.latitude}%2C${coordinates.longitude}`
      : null;
  const openStreetMapHref =
    coordinates
      ? `https://www.openstreetmap.org/?mlat=${coordinates.latitude}&mlon=${coordinates.longitude}#map=15/${coordinates.latitude}/${coordinates.longitude}`
      : null;

  return (
    <Sheet open={record !== null} onOpenChange={(open) => !open && onClose()}>
      {record && (
        <>
          <SheetHeader onClose={onClose}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{record.source}</Badge>
              <SheetTitle>{participantLabel}</SheetTitle>
              <span className="text-xs text-muted-foreground">{deviceLabel}</span>
            </div>
            <SheetDescription>
              Collected {formatDate(record.collected_at)} · uploaded{" "}
              {relativeTime(record.uploaded_at)}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <div className="space-y-4">
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Metadata
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs font-mono">
                  <dt className="text-muted-foreground">participant</dt>
                  <dd className="truncate" title={record.participant_id}>
                    {record.participant_id}
                  </dd>
                  <dt className="text-muted-foreground">device</dt>
                  <dd className="truncate" title={record.device_id ?? ""}>
                    {record.device_id || "—"}
                  </dd>
                  <dt className="text-muted-foreground">source</dt>
                  <dd>{record.source}</dd>
                  <dt className="text-muted-foreground">collected_at</dt>
                  <dd title={record.collected_at}>
                    {isHealthConnect ? formatDateTimeMst(record.collected_at) : record.collected_at}
                  </dd>
                  <dt className="text-muted-foreground">uploaded_at</dt>
                  <dd title={record.uploaded_at}>
                    {isHealthConnect ? formatDateTimeMst(record.uploaded_at) : record.uploaded_at}
                  </dd>
                  {record.uuid && (
                    <>
                      <dt className="text-muted-foreground">uuid</dt>
                      <dd className="truncate" title={record.uuid}>
                        {record.uuid}
                      </dd>
                    </>
                  )}
                </dl>
              </section>
              {isSurvey && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Survey Responses
                  </h3>
                  <div className="space-y-2">
                    {getSurveyResponses(record).map(({ key, question, answer }) => (
                      <div key={key} className="rounded border bg-muted/20 px-3 py-2">
                        <div className="text-xs text-muted-foreground">{question}</div>
                        <div className="text-sm font-medium">{answer}</div>
                      </div>
                    ))}
                  </div>
                  <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs font-mono">
                    {record.values.slot_id != null && (
                      <>
                        <dt className="text-muted-foreground">slot</dt>
                        <dd>{String(record.values.slot_id)}</dd>
                      </>
                    )}
                    {record.values.config_version != null && (
                      <>
                        <dt className="text-muted-foreground">config_version</dt>
                        <dd>{String(record.values.config_version)}</dd>
                      </>
                    )}
                  </dl>
                </section>
              )}
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Values
                </h3>
                <JsonTree data={valuesForDisplay} />
              </section>
              {coordinates && mapSrc && openStreetMapHref && (
                <section>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Location Map
                    </h3>
                    <a
                      className="text-xs font-medium text-[#2563EB] hover:underline"
                      href={openStreetMapHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in map
                    </a>
                  </div>
                  <div className="overflow-hidden rounded-lg border bg-muted/20">
                    <iframe
                      title="AT_LOCATION map"
                      src={mapSrc}
                      className="h-72 w-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </section>
              )}
            </div>
          </SheetBody>
        </>
      )}
    </Sheet>
  );
}
